// Datenzugriff für Rechnungskorrekturen. Die Regeln liegen in korrektur.ts (ohne
// Datenbankbezug und dort getestet) — hier steht nur, was Supabase braucht.
import { supabase } from '../supabase/client';
import { anschriftHtml } from './rechnungsanschrift';
import type { EigentuemerNameFelder } from '../eigentuemer/anzeige';
import { addTimelineEvent } from './timeline';
import { driveFileIdAusUrl } from './weiterleitung';
import { darfKorrekturAnfordern, korrekturEmpfaenger, korrekturDateiname } from './korrektur';

const KORREKTUR_TRIGGER = 'rechnung_korrektur';

export type KorrekturFehlerGrund = 'nicht_gefunden' | 'keine_datei' | 'keine_vorlage' | 'nicht_erlaubt';

export interface KorrekturCompose {
  /** false = kein Eigentuemer an der Rechnung; die Anschrift traegt dann keinen Namen. */
  hatEigentuemer?: boolean;
  ok: true;
  rechnungId: string;
  rechnungsnummer: string;
  /** null = keine brauchbare Adresse gefunden; der Nutzer trägt sie in der Pille ein. */
  empfaenger: string | null;
  driveFileId: string;
  dateiname: string;
  /**
   * Werte für die Platzhalter der Vorlage. MUSS mitgeliefert werden: die automatische
   * Auflösung in EmailVerfassen (ladePlatzhalterWerte) kennt nur `mieter.*` und
   * `objekt.*` — ein `{{rechnung.nummer}}` stünde sonst wörtlich in der Mail an den
   * Lieferanten. Der Grund kommt erst aus dem Dialog dazu.
   */
  vorlagenWerte: Record<string, string>;
}

export interface KorrekturFehler {
  ok: false;
  grund: KorrekturFehlerGrund;
  text: string;
}

export interface KurzRechnung {
  id: string;
  rechnungsnummer: string;
  datum: string | null;
  brutto: number | null;
}

/**
 * Betrag mit Waehrung — toLocaleString WIRFT einen RangeError, wenn `waehrung` kein
 * ISO-Code ist (im Bestand steht dort auch schon mal „€"). Der Fehler kippte die
 * ganze Korrekturanfrage: handleKorrekturAnfordern hat nur try/finally, die Pille
 * ginge dann wortlos nicht auf.
 */
function bruttoText(brutto?: number | null, waehrung?: string | null): string {
  if (brutto == null) return '';
  const code = (waehrung || '').trim().toUpperCase();
  try {
    return brutto.toLocaleString('de-DE', { style: 'currency', currency: /^[A-Z]{3}$/.test(code) ? code : 'EUR' });
  } catch {
    return `${brutto.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €`;
  }
}

/**
 * Alles laden, was die Verfassen-Pille für die Korrekturanfrage braucht.
 *
 * Die Empfängeradresse kommt aus der Ursprungsmail (rechnungen.dokument_id →
 * dokumente.quelle_email_id → emails.von): 278 der 896 Rechnungen haben diesen Bezug und
 * alle davon führen zur Absenderadresse, über die Dienstleister-Stammdaten wären nur 21
 * von 243 Lieferanten erreichbar.
 */
export async function baueKorrekturCompose(rechnungId: string, ausPool = false): Promise<KorrekturCompose | KorrekturFehler> {
  // Pool-Belege (rechnungen_unklar) koennen dasselbe, nur kennen sie weder dokument_id
  // noch korrigiert_durch_id — die Spalten gibt es dort nicht (Ticket #419).
  const { data: r } = ausPool
    ? await supabase.from('rechnungen_unklar')
      .select('id, rechnungsnummer, unternehmen, datum, brutto, waehrung, datei_url, eigentuemer_id, archiviert_am, korrektur_angefordert_am')
      .eq('id', rechnungId).maybeSingle()
    : await supabase.from('rechnungen')
      .select('id, rechnungsnummer, unternehmen, datum, brutto, waehrung, datei_url, dokument_id, eigentuemer_id, archiviert_am, korrektur_angefordert_am, korrigiert_durch_id')
      .eq('id', rechnungId).maybeSingle();
  if (!r) return { ok: false, grund: 'nicht_gefunden', text: 'Beleg nicht gefunden.' };
  if (!darfKorrekturAnfordern(r)) {
    return { ok: false, grund: 'nicht_erlaubt', text: 'Für diese Rechnung ist bereits eine Korrektur angefordert oder eingetragen.' };
  }

  const driveFileId = driveFileIdAusUrl(r.datei_url);
  if (!driveFileId) return { ok: false, grund: 'keine_datei', text: 'Zu dieser Rechnung liegt keine Google-Drive-PDF vor.' };

  const { data: vorlage } = await supabase.from('email_vorlagen')
    .select('id').eq('ki_trigger', KORREKTUR_TRIGGER).maybeSingle();
  if (!vorlage) return { ok: false, grund: 'keine_vorlage', text: 'Die Vorlage „Rechnungskorrektur anfordern" wurde nicht gefunden.' };

  // Adresse der Ursprungsmail
  let quellMail: string | null = null;
  const dokumentId = ausPool ? null : (r as unknown as { dokument_id?: string | null }).dokument_id || null;
  if (dokumentId) {
    const { data: dok } = await supabase.from('dokumente')
      .select('quelle_email_id').eq('id', dokumentId).maybeSingle();
    if (dok?.quelle_email_id) {
      const { data: mail } = await supabase.from('emails')
        .select('von').eq('id', dok.quelle_email_id).maybeSingle();
      quellMail = mail?.von || null;
    }
  }

  // Rückfallebene: Dienstleister mit gleichem Namen
  let dienstleisterMail: string | null = null;
  if (!quellMail && r.unternehmen) {
    const { data: dl } = await supabase.from('dienstleister')
      .select('email').ilike('name', r.unternehmen.trim()).limit(1).maybeSingle();
    dienstleisterMail = dl?.email || null;
  }

  // Rechnungsanschrift fuer die Vorlage (Ticket #423): Der Lieferant soll die korrigierte
  // Rechnung auf den Eigentuemer ausstellen, zugestellt ueber die Verwaltung. Ohne
  // hinterlegten Eigentuemer bleibt die Namenszeile leer statt "(Eigentuemer)".
  let eigentuemer: EigentuemerNameFelder | null = null;
  const eigentuemerId = (r as unknown as { eigentuemer_id?: string | null }).eigentuemer_id || null;
  if (eigentuemerId) {
    const { data: e } = await supabase.from('eigentuemer')
      .select('anrede, vorname, nachname, firma_name').eq('id', eigentuemerId).maybeSingle();
    eigentuemer = (e as EigentuemerNameFelder) || null;
  }

  const nummer = r.rechnungsnummer || 'Rechnung';
  return {
    ok: true,
    rechnungId: r.id,
    rechnungsnummer: nummer,
    empfaenger: korrekturEmpfaenger(quellMail, dienstleisterMail),
    driveFileId,
    dateiname: korrekturDateiname(r.rechnungsnummer),
    vorlagenWerte: {
      'rechnung.nummer': nummer,
      'rechnung.datum': r.datum ? new Date(r.datum).toLocaleDateString('de-DE') : '',
      'rechnung.brutto': bruttoText(r.brutto, r.waehrung),
      'rechnung.empfaenger_anschrift': anschriftHtml(eigentuemer),
    },
    hatEigentuemer: !!eigentuemer,
  };
}

/**
 * Nach erfolgreichem Versand: Rechnung markieren + Timeline.
 * Ein Fehler in der Timeline darf die Markierung nicht kippen (Muster
 * markiereWeitergeleitet) — sie ist es, die die Rechnungsnummer freigibt.
 */
export async function markiereKorrekturAngefordert(opts: {
  rechnungId: string;
  grund: string;
  empfaenger: string;
  userId?: string | null;
  ausPool?: boolean;
}): Promise<void> {
  const { error } = await supabase.from(opts.ausPool ? 'rechnungen_unklar' : 'rechnungen').update({
    korrektur_angefordert_am: new Date().toISOString(),
    korrektur_grund: opts.grund.trim() || null,
  }).eq('id', opts.rechnungId);
  if (error) throw error;

  // Der Pool hat keinen Verlauf (rechnungen_timeline haengt per FK an `rechnungen`).
  if (opts.userId && !opts.ausPool) {
    try {
      await addTimelineEvent(
        opts.rechnungId, 'korrektur_angefordert', opts.userId,
        `Korrektur bei ${opts.empfaenger} angefordert${opts.grund.trim() ? ` — ${opts.grund.trim()}` : ''}`,
      );
    } catch (e) { console.warn('Timeline-Eintrag (Korrekturanfrage) fehlgeschlagen', e); }
  }
}

/**
 * Anforderung zurücknehmen: kommt die Korrektur nie, muss der Beleg zurück in die
 * Eindeutigkeit. Ohne diesen Weg bliebe er dauerhaft aus beiden Teil-Indizes
 * ausgenommen — der Doppelbuchungsschutz waere fuer ihn fuer immer aus, und der
 * einzige Ausweg waere das Archivieren, das diese Etappe gerade abschaffen wollte.
 */
export async function ziehKorrekturAnforderungZurueck(
  rechnungId: string, userId?: string | null, ausPool = false,
): Promise<void> {
  // Kann selbst in einen der Teil-Indizes laufen, wenn die Nachfolgerin schon erfasst
  // ist: ohne korrektur_angefordert_am zaehlt der Beleg wieder mit. Der Aufrufer
  // uebersetzt den 23505 mit korrekturFehlerText.
  const { error } = await supabase.from(ausPool ? 'rechnungen_unklar' : 'rechnungen')
    .update({ korrektur_angefordert_am: null, korrektur_grund: null })
    .eq('id', rechnungId);
  if (error) throw error;

  if (userId && !ausPool) {
    try {
      await addTimelineEvent(rechnungId, 'korrektur_zurueckgezogen', userId, 'Korrekturanfrage zurückgezogen');
    } catch (e) { console.warn('Timeline-Eintrag (Korrektur zurückgezogen) fehlgeschlagen', e); }
  }
}

/** Nachfolgerin eintragen. Die Gegenrichtung wird nicht gespeichert (Spec). */
export async function verknuepfeKorrektur(
  vorgaengerId: string, nachfolgerId: string, userId?: string | null,
): Promise<void> {
  if (vorgaengerId === nachfolgerId) throw new Error('Eine Rechnung kann nicht ihre eigene Korrektur sein.');

  // Leitplanken, die die Datenbank nicht zieht: auf korrigiert_durch_id liegt kein
  // Unique-Index, also waeren zwei Vorgaengerinnen auf denselben Beleg und der Zyklus
  // A↔B moeglich. Beides ergibt im Verlauf keinen Sinn und bricht die Rueckwaerts-Anzeige.
  const [{ data: nachfolger }, { data: schonBelegt }] = await Promise.all([
    supabase.from('rechnungen').select('id, rechnungsnummer, korrigiert_durch_id')
      .eq('id', nachfolgerId).maybeSingle(),
    supabase.from('rechnungen').select('id, rechnungsnummer')
      .eq('korrigiert_durch_id', nachfolgerId).neq('id', vorgaengerId).limit(1).maybeSingle(),
  ]);
  if (!nachfolger) throw new Error('Die gewählte Rechnung wurde nicht gefunden.');
  if (schonBelegt) {
    throw new Error(
      `Diese Rechnung ist bereits als Korrektur von ${schonBelegt.rechnungsnummer || 'einer anderen Rechnung'} eingetragen.`,
    );
  }
  if (nachfolger.korrigiert_durch_id === vorgaengerId) {
    throw new Error('Diese Rechnung wird bereits durch die aktuelle Rechnung ersetzt — beide Richtungen zugleich sind nicht möglich.');
  }

  const { error } = await supabase.from('rechnungen')
    .update({ korrigiert_durch_id: nachfolgerId }).eq('id', vorgaengerId);
  if (error) throw error;
  if (userId) {
    try {
      await addTimelineEvent(
        vorgaengerId, 'korrektur_verknuepft', userId,
        `Korrigierte Rechnung ${nachfolger.rechnungsnummer || ''}`.trim() + ' zugeordnet',
      );
    } catch (e) { console.warn('Timeline-Eintrag (Korrektur verknüpft) fehlgeschlagen', e); }
  }
}

/** Verknüpfung lösen — die Zuordnung kann danebengegriffen haben (Risiko in der Spec). */
export async function loeseKorrekturVerknuepfung(vorgaengerId: string): Promise<void> {
  const { error } = await supabase.from('rechnungen')
    .update({ korrigiert_durch_id: null }).eq('id', vorgaengerId);
  if (error) throw error;
}

/** Beide Richtungen für die Anzeige: meine Nachfolgerin und die Rechnung, die ich ersetze. */
export async function ladeKorrekturPartner(
  rechnung: { id: string; korrigiert_durch_id?: string | null },
): Promise<{ nachfolger: KurzRechnung | null; vorgaenger: KurzRechnung | null }> {
  const felder = 'id, rechnungsnummer, datum, brutto';
  const [nach, vor] = await Promise.all([
    rechnung.korrigiert_durch_id
      ? supabase.from('rechnungen').select(felder).eq('id', rechnung.korrigiert_durch_id).maybeSingle()
      : Promise.resolve({ data: null }),
    // limit(1): maybeSingle() WIRFT bei mehr als einer Zeile. Auf korrigiert_durch_id
    // liegt kein Unique-Index — zwei Vorgaengerinnen sind unerwuenscht, aber moeglich,
    // und duerfen die Anzeige nicht sprengen.
    supabase.from('rechnungen').select(felder).eq('korrigiert_durch_id', rechnung.id).limit(1).maybeSingle(),
  ]);
  return {
    nachfolger: (nach.data as KurzRechnung) || null,
    vorgaenger: (vor.data as KurzRechnung) || null,
  };
}
